import { ApproveTokenButton } from "components/ApproveTokenButton/ApproveTokenButton";
import { getContract } from "config/contracts";
import { ethers } from "ethers";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";

import Footer from "components/Footer/Footer";
import Modal from "components/Modal/Modal";

import RewardRouter from "sdk/abis/RewardRouter.json";
import RewardTracker from "sdk/abis/RewardTracker.json";
import Token from "sdk/abis/Token.json";
import Vester from "sdk/abis/Vester.json";

import { FaCheck, FaTimes } from "react-icons/fa";

import { Trans, t } from "@lingui/macro";

import Button from "components/Button/Button";
import Checkbox from "components/Checkbox/Checkbox";
import { getNeedTokenApprove, useTokenBalances, useTokensAllowanceData } from "domain/synthetics/tokens";
import { approveTokens } from "domain/tokens";
import { useChainId } from "lib/chains";
import { callContract, contractFetcher } from "lib/contracts";
import { usePendingTxns } from "lib/usePendingTxns";
import useWallet from "lib/wallets/useWallet";
import { zeroAddress } from "viem";
import "./BeginAccountTransfer.css";

function ValidationRow({ isValid, children }) {
  return (
    <div className="ValidationRow">
      <div className="ValidationRow-icon-container">
        {isValid && <FaCheck className="ValidationRow-icon" />}
        {!isValid && <FaTimes className="ValidationRow-icon" />}
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function BeginAccountTransfer() {
  const { active, signer, account } = useWallet();
  const { chainId } = useChainId();
  const [, setPendingTxns] = usePendingTxns();
  const [receiver, setReceiver] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isTransferSubmittedModalVisible, setIsTransferSubmittedModalVisible] = useState(false);
  const [isAffiliateVesterSkipValidation, setIsAffiliateVesterSkipValidation] = useState(false);
  let parsedReceiver = ethers.ZeroAddress;
  if (ethers.isAddress(receiver)) {
    parsedReceiver = receiver;
  }

  const moneyAddress = getContract(chainId, "MONEY");
  const moneyVesterAddress = getContract(chainId, "MoneyVester");
  const mlpVesterAddress = getContract(chainId, "MlpVester");
  const affiliateVesterAddress = getContract(chainId, "AffiliateVester");

  const rewardRouterAddress = getContract(chainId, "RewardRouter");

  const { data: moneyVesterBalance } = useSWR(active && [active, chainId, moneyVesterAddress, "balanceOf", account], {
    fetcher: contractFetcher(signer, Token),
  });

  const { data: mlpVesterBalance } = useSWR(active && [active, chainId, mlpVesterAddress, "balanceOf", account], {
    fetcher: contractFetcher(signer, Token),
  });

  const { data: affiliateVesterBalance } = useSWR(
    active && [active, chainId, affiliateVesterAddress, "balanceOf", account],
    {
      fetcher: contractFetcher(signer, Token),
    }
  );

  const stakedMoneyTrackerAddress = getContract(chainId, "StakedMoneyTracker");
  const { data: cumulativeMoneyRewards } = useSWR(
    [active, chainId, stakedMoneyTrackerAddress, "cumulativeRewards", parsedReceiver],
    {
      fetcher: contractFetcher(signer, RewardTracker),
    }
  );

  const bonusMoneyTrackerAddress = getContract(chainId, "BonusMoneyTracker");
  const { data: cumulativeBonusMoneyTrackerRewards } = useSWR(
    [active, chainId, bonusMoneyTrackerAddress, "cumulativeRewards", parsedReceiver],
    {
      fetcher: contractFetcher(signer, RewardTracker),
    }
  );

  const feeMlpTrackerAddress = getContract(chainId, "FeeMlpTracker");
  const { data: cumulativeFeeMlpTrackerRewards } = useSWR(
    [active, chainId, feeMlpTrackerAddress, "cumulativeRewards", parsedReceiver],
    {
      fetcher: contractFetcher(signer, RewardTracker),
    }
  );

  const stakedMlpTrackerAddress = getContract(chainId, "StakedMlpTracker");
  const { data: cumulativeMlpRewards } = useSWR(
    [active, chainId, stakedMlpTrackerAddress, "cumulativeRewards", parsedReceiver],
    {
      fetcher: contractFetcher(signer, RewardTracker),
    }
  );

  const { data: transferredCumulativeMoneyRewards } = useSWR(
    [active, chainId, moneyVesterAddress, "transferredCumulativeRewards", parsedReceiver],
    {
      fetcher: contractFetcher(signer, Vester),
    }
  );

  const { data: transferredCumulativeMlpRewards } = useSWR(
    [active, chainId, mlpVesterAddress, "transferredCumulativeRewards", parsedReceiver],
    {
      fetcher: contractFetcher(signer, Vester),
    }
  );

  const { data: pendingReceiver } = useSWR(
    active && [active, chainId, rewardRouterAddress, "pendingReceivers", account],
    {
      fetcher: contractFetcher(signer, RewardRouter),
    }
  );

  const { tokensAllowanceData: moneyAllowanceData } = useTokensAllowanceData(chainId, {
    spenderAddress: stakedMoneyTrackerAddress,
    tokenAddresses: [moneyAddress],
  });
  const moneyAllowance = moneyAllowanceData?.[moneyAddress];

  const { data: moneyStaked } = useSWR(
    active && [active, chainId, stakedMoneyTrackerAddress, "depositBalances", account, moneyAddress],
    {
      fetcher: contractFetcher(signer, RewardTracker),
    }
  );

  const needApproval = moneyAllowance !== undefined && moneyStaked && moneyStaked > moneyAllowance;

  const hasVestedMoney = moneyVesterBalance > 0;
  const hasVestedMlp = mlpVesterBalance > 0;
  const hasVestedAffiliate = affiliateVesterBalance > 0;
  const hasStakedMoney =
    (cumulativeMoneyRewards && cumulativeMoneyRewards > 0) ||
    (transferredCumulativeMoneyRewards && transferredCumulativeMoneyRewards > 0) ||
    (cumulativeBonusMoneyTrackerRewards && cumulativeBonusMoneyTrackerRewards > 0);
  const hasStakedMlp =
    (cumulativeMlpRewards && cumulativeMlpRewards > 0) ||
    (transferredCumulativeMlpRewards && transferredCumulativeMlpRewards > 0) ||
    (cumulativeFeeMlpTrackerRewards && cumulativeFeeMlpTrackerRewards > 0);
  const hasPendingReceiver = pendingReceiver && pendingReceiver !== ethers.ZeroAddress;

  const getError = () => {
    if (!account) {
      return t`Wallet is not connected`;
    }
    if (hasVestedMoney) {
      return t`Vested MONEY not withdrawn`;
    }
    if (hasVestedMlp) {
      return t`Vested MLP not withdrawn`;
    }
    if (!receiver || receiver.length === 0) {
      return t`Enter Receiver Address`;
    }
    if (!ethers.isAddress(receiver)) {
      return t`Invalid Receiver Address`;
    }

    if (hasVestedAffiliate && !isAffiliateVesterSkipValidation) {
      return t`Vested MONEY not withdrawn`;
    }

    if (hasStakedMoney || hasStakedMlp) {
      return t`Receiver has staked MONEY/MLP before`;
    }

    if ((parsedReceiver || "").toString().toLowerCase() === (account || "").toString().toLowerCase()) {
      return t`Self-transfer not supported`;
    }

    if (
      (parsedReceiver || "").length > 0 &&
      (parsedReceiver || "").toString().toLowerCase() === (pendingReceiver || "").toString().toLowerCase()
    ) {
      return t`Transfer already initiated`;
    }
  };

  const isReadyForSbfMoneyTokenApproval = !(
    hasVestedMlp ||
    hasVestedMoney ||
    (hasVestedAffiliate && !isAffiliateVesterSkipValidation) ||
    hasStakedMoney ||
    hasStakedMlp
  );

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isApproving) {
      return false;
    }
    if (isTransferring) {
      return false;
    }

    if (needFeeMoneyTrackerApproval) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (needApproval) {
      return t`Approve MONEY`;
    }
    if (isApproving) {
      return t`Approving...`;
    }
    if (isTransferring) {
      return t`Transferring`;
    }

    if (needFeeMoneyTrackerApproval) {
      return t`Pending Transfer Approval`;
    }

    return t`Begin Transfer`;
  };

  const onClickPrimary = () => {
    if (needApproval) {
      approveTokens({
        setIsApproving,
        signer,
        tokenAddress: moneyAddress,
        spender: stakedMoneyTrackerAddress,
        chainId,
      });
      return;
    }

    setIsTransferring(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, signer);

    callContract(chainId, contract, "signalTransfer", [parsedReceiver], {
      sentMsg: t`Transfer submitted!`,
      failMsg: t`Transfer failed.`,
      setPendingTxns,
    })
      .then(() => {
        setIsTransferSubmittedModalVisible(true);
      })
      .finally(() => {
        setIsTransferring(false);
      });
  };

  const completeTransferLink = `/complete_account_transfer/${account}/${parsedReceiver}`;
  const pendingTransferLink = `/complete_account_transfer/${account}/${pendingReceiver}`;

  const feeMoneyTrackerAddress = getContract(chainId, "FeeMoneyTracker");

  const { tokensAllowanceData: feeMoneyAllowanceData } = useTokensAllowanceData(chainId, {
    spenderAddress: parsedReceiver,
    tokenAddresses: [feeMoneyTrackerAddress],
  });
  const { balancesData } = useTokenBalances(chainId, undefined, [{ address: feeMoneyTrackerAddress }], 1000);

  const feeMoneyTrackerBalance = balancesData?.[feeMoneyTrackerAddress];
  const needFeeMoneyTrackerApproval = useMemo(
    () =>
      Boolean(
        parsedReceiver &&
          feeMoneyTrackerBalance !== undefined &&
          parsedReceiver !== zeroAddress &&
          feeMoneyAllowanceData &&
          getNeedTokenApprove(feeMoneyAllowanceData, feeMoneyTrackerAddress, feeMoneyTrackerBalance)
      ),
    [feeMoneyTrackerAddress, feeMoneyTrackerBalance, parsedReceiver, feeMoneyAllowanceData]
  );

  return (
    <div className="BeginAccountTransfer Page page-layout ">
      <Modal
        isVisible={isTransferSubmittedModalVisible}
        setIsVisible={setIsTransferSubmittedModalVisible}
        label={t`Transfer Submitted`}
      >
        <Trans>Your transfer has been initiated.</Trans>
        <br />
        <br />
        <Link className="App-cta" to={completeTransferLink}>
          <Trans>Continue</Trans>
        </Link>
      </Modal>

      <div className="default-container !m-0 pb-16">
        <div className="Page-title">
          <Trans>Transfer Account</Trans>
        </div>
        <div className="Page-description">
          <Trans>
            Please only use this for full account transfers.
            <br />
            This will transfer all your MONEY, esMONEY, MLP, Multiplier Points and voting power to your new account.
            <br />
            Transfers are only supported if the receiving account has not staked MONEY or MLP tokens before.
            <br />
            Transfers are one-way, you will not be able to transfer staked tokens back to the sending account.
          </Trans>
        </div>
        {hasPendingReceiver && (
          <div className="Page-description">
            <Trans>
              You have a <Link to={pendingTransferLink}>pending transfer</Link> to {pendingReceiver}.
            </Trans>
          </div>
        )}
      </div>
      <div className="default-container !m-0">
        <div className="input-form">
          <div className="input-row">
            <label className="input-label">
              <Trans>Receiver Address</Trans>
            </label>
            <div>
              <input
                type="text"
                value={receiver}
                onChange={(e) => setReceiver(e.target.value)}
                className="text-input"
              />
            </div>
          </div>
          <div className="BeginAccountTransfer-validations">
            <ValidationRow isValid={!hasVestedMoney}>
              <Trans>Sender has withdrawn all tokens from MONEY Vesting Vault</Trans>
            </ValidationRow>
            <ValidationRow isValid={!hasVestedMlp}>
              <Trans>Sender has withdrawn all tokens from MLP Vesting Vault</Trans>
            </ValidationRow>
            <ValidationRow isValid={!hasVestedAffiliate}>
              <Trans>Sender has withdrawn all tokens from Affiliate Vesting Vault</Trans>
            </ValidationRow>
            {hasVestedAffiliate && (
              <>
                <p className="soft-error">
                  <Trans>
                    You have esMONEY tokens in the Affiliate Vault, you need to withdraw these tokens if you want to
                    transfer them to the new account
                  </Trans>
                </p>
                <Checkbox
                  className="VestedAffiliate-checkbox"
                  isChecked={isAffiliateVesterSkipValidation}
                  setIsChecked={setIsAffiliateVesterSkipValidation}
                >
                  <span className="text-sm text-yellow-500">
                    <Trans>I do not want to transfer the Affiliate esMONEY tokens</Trans>
                  </span>
                </Checkbox>
              </>
            )}

            <ValidationRow isValid={!hasStakedMoney}>
              <Trans>Receiver has not staked MONEY tokens before</Trans>
            </ValidationRow>
            <ValidationRow isValid={!hasStakedMlp}>
              <Trans>Receiver has not staked MLP tokens before</Trans>
            </ValidationRow>
          </div>

          {isReadyForSbfMoneyTokenApproval && needFeeMoneyTrackerApproval && (
            <>
              <ApproveTokenButton
                tokenAddress={feeMoneyTrackerAddress}
                tokenSymbol={"sbfMONEY"}
                customLabel={t`Allow all my tokens to be transferred to a new account`}
                spenderAddress={parsedReceiver}
                approveAmount={feeMoneyTrackerBalance}
              />
              <br />
            </>
          )}
          <div className="input-row">
            <Button
              variant="primary-action"
              className="w-full"
              disabled={!isPrimaryEnabled()}
              onClick={() => onClickPrimary()}
            >
              {getPrimaryText()}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
